import { useAuthContext } from "@kalyzee/kast-web-sdk";
import { useCallback, useEffect } from "react";
import { getCompatibilityInfos } from "../../helpers/sdk";
import { toastError } from "../../helpers/toast";
import { SocketEventType } from "@kalyzee/kast-web-sdk/websocket";
import { AppStore } from "./store";
import { useAppContext } from "../app/context";
import { useSdkContext } from "../sdk/context";
import { MOBILE_BREAKPOINT } from "../../interfaces/ui";

///////////////////////
//////// TYPES ////////
///////////////////////

export enum SessionMode {
  MASTER = 'master',
  SLAVE = 'slave'
}

export type UserFetchStatus = "connecting" | "connected" | "disconnected";

/////////////////////////
////// UTILS HOOKS //////
/////////////////////////

const useFetchUser = (store: AppStore) => {
  const sdkCtx = useSdkContext();
  const fetchUser = useCallback(async () => {
    try {
      store.dispatch("setUserFetchStatus", "connecting");
      const response = await sdkCtx.client.getHttpClient().user.getMe();
      store.dispatch("setUser", response.data);
      store.dispatch("setUserFetchStatus", "connected");
    } catch (error) {
      console.error(`[ERROR] requestGetMe: `, error);
      store.dispatch("setUserFetchStatus", "disconnected");
      toastError(`Une erreur est apparue.`);
    }
  }, [store]);
  return fetchUser;
}

export const StoreUtilsHooks = {
  useFetchUser,
};

/////////////////////////
////// STORE HOOKS //////
/////////////////////////

const useUser = (store: AppStore) => {
  const { status: authStatus } = useAuthContext();

  const fetchUser = useFetchUser(store);

  useEffect(() => {
    if (authStatus === "in") fetchUser();
  }, [authStatus]);
};

const useCompatibilityInfo = (store: AppStore) => {
  const sdkCtx = useSdkContext();
  useEffect(() => {
    const ws = sdkCtx.client.getWebsocketClient();
    const handleConnect = async () => {
      const infos = await getCompatibilityInfos(sdkCtx.client);
      store.dispatch("setCompatibilityInfos", infos);
    };
    ws.on(SocketEventType.Connect, handleConnect);
    return () => ws.off(SocketEventType.Connect, handleConnect);
  }, []);
};

const useMainClock = (store: AppStore) => {
  useEffect(() => {
    const interval = setInterval(() => {
      store.dispatch("setMainClock", new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
};

const useMobileDetection = (store: AppStore) => {
  useEffect(() => {
    const handleResize = () => {
      // Don't update isMobile while in fullscreen — the viewport change is
      // temporary and flipping the layout would unmount the fullscreen element.
      if (document.fullscreenElement) return;
      store.dispatch("setIsMobile", window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [store]);
};

export const StoreHooks = {
  useUser,
  useCompatibilityInfo,
  useMainClock,
  useMobileDetection,
};
